import { AppDispatch } from "app/store";
import { COUNTRY_JP } from "common/constants";
import OmronFooter from "components/OmronFooter";
import OmronHeader from "components/OmronHeader";
import OmronSidebar from "components/OmronSidebar";
import { logoutAsync, selectUserState, userAsync } from "features/auth";
import { initialAsync, selectCountry, selectEmail } from "features/profile";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import ProfileInputJapan from "./ProfileInputJapan";
import ProfileInputOthers from "./ProfileInputOthers";

const ProfilePage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement>(null);
  const user = useSelector(selectUserState);
  const country = useSelector(selectCountry);
  const email = useSelector(selectEmail);
  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);
  // 初期読込
  useEffect(() => {
    const isCloseSnackbar = location?.state?.from !== "changePassword";
    dispatch(userAsync({ isCloseSnackbar }));
    dispatch(initialAsync({ isCloseSnackbar }));
  }, [dispatch, location]);
  return (
    <MainWrapper>
      <OmronHeader
        link={{ path: "/", label: t("common.label.breadcrumbsSignin") }}
        label={[t("common.label.breadcrumbsProfile")]}
        user={user}
      />
      <OmronSidebar profile onLogout={logout} />
      <Main ref={mainRef}>
        <TitleWrapper>
          <Title>{t("profilePage.label.accountInfo")}</Title>
          <Text />
        </TitleWrapper>
        <Section>
          <AccountInfoDl>
            <AccountInfoDt> {t("profilePage.label.email")}</AccountInfoDt>
            <AccountInfoDd>{email}</AccountInfoDd>
            <PasswordDt>{t("profilePage.label.password")}</PasswordDt>
            <PasswordDd>
              ●●●●●●●●
              <LinkWrapper>
                <Link href="/profile/changePassword">
                  {t("profilePage.label.changePasswordButton")}
                </Link>
              </LinkWrapper>
            </PasswordDd>
          </AccountInfoDl>
        </Section>
        {country === COUNTRY_JP ? (
          <ProfileInputJapan scrollOffset={mainRef.current?.offsetTop ?? 0} />
        ) : (
          <ProfileInputOthers scrollOffset={mainRef.current?.offsetTop ?? 0} />
        )}
      </Main>
      <OmronFooter />
    </MainWrapper>
  );
};

export default ProfilePage;

const MainWrapper = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas:
    "header header"
    "breadc breadc"
    "side main"
    "side footer";
  min-height: 100vh;
`;
const Main = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;
const TitleWrapper = styled.div`
  margin-bottom: 3rem;
`;
const Title = styled.h1`
  margin-bottom: 3rem;
`;
const Text = styled.p`
  margin-bottom: 3rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;
const AccountInfoDl = styled.dl`
  margin-bottom: 3rem;
  border-bottom: 0.1rem solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-wrap: wrap;
`;

const AccountInfoDt = styled.dt`
  width: 15rem;
  padding: 1.3rem 0.5rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;

const PasswordDt = styled(AccountInfoDt)`
  margin-bottom: 2rem;
`;

const AccountInfoDd = styled.dd`
  width: calc(100% - 17rem);
  padding: 1.3rem 0.5rem;
  position: relative;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;

const PasswordDd = styled(AccountInfoDd)`
  margin-bottom: 2rem;
`;

const LinkWrapper = styled.p`
  display: inline-block;
  position: absolute;
  left: 30rem;
  top: 50%;
  transform: translateY(-50%);
`;
const Link = styled.a`
  display: inline-block;
  color: #005eb8;
  background: #ffffff;
  border: 1px #005eb8 solid;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  width: auto;
  padding: 0.75rem 2rem;
  :hover {
    background: rgba(0, 94, 184, 0.04);
  }
`;
const Section = styled.section`
  display: block;
`;
